import React, { FC, useMemo } from 'react';
import type { StylesConfig } from 'react-select';

import { useFormikContext } from 'formik';

import SearchableSelect, {
    SearchableSelectType,
} from 'components/inputs/searchableSelect';
import InlineNotification from 'components/snackBar/InlineNotification';
import styled from 'styled-components';

import aampeTheme from '../../../constants/aampeTheme';
import ICONS from '../../../constants/icons';
import { ToastStatus } from '../../../constants/toastMessage.constants';

import { getLabelFromSectionType } from './helpers';

import { ComponentTypeTransformed } from 'models/component-types-model';
import { DatasetModel } from 'models/dataset.model';
import { SectionElement } from 'models/editor.model';

import defaultStyleConfig from '../../shared/AampeCreateSelectDropdown/select-style';
import MapElementContent from './MapContent/MapElementContent';

interface MapElementProps {
    element: SectionElement;
    componentTypes: ComponentTypeTransformed;
    datasetData: DatasetModel;
    mapOptions: SearchableSelectType[];
    showMappingErrors?: boolean;
}

const MapElement: FC<MapElementProps> = ({
    element,
    componentTypes,
    datasetData,
    mapOptions,
    showMappingErrors,
}) => {
    const { values, setFieldValue } = useFormikContext();

    const heading = useMemo(() => {
        if (element.sectionType) {
            return getLabelFromSectionType(element.sectionType);
        }
    }, [element]);

    const alreadyUsedValues = useMemo(() => {
        return Object.values(values || {}).map((item) => item?.value);
    }, [values]);

    const filteredOptions = useMemo(() => {
        return mapOptions.filter((opt) => {
            // Always keep "None"
            if (opt.value === null) return true;

            if (typeof opt.value === 'string') {
                const [fieldName, modifier] = opt.value.split('--');

                // case: modifier exists → match by modifier
                if (modifier && element.sectionType) {
                    return (
                        element.sectionType.includes(modifier) &&
                        !alreadyUsedValues.includes(opt.value)
                    );
                }

                // case: no modifier → match by fieldName
                return (
                    element?.sectionType === fieldName &&
                    !alreadyUsedValues.includes(opt.value)
                );
            }

            return false;
        });
    }, [mapOptions, element, alreadyUsedValues]);

    const fieldValue = (values as any)[element.sectionType];
    const isMissing =
        !!showMappingErrors && (fieldValue === '' || fieldValue === undefined);
    const errorStyles: StylesConfig<SearchableSelectType> | undefined =
        isMissing
            ? {
                  control: (provided, state) => ({
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      ...((defaultStyleConfig.control as any)?.(
                          provided,
                          state,
                      ) ?? provided),
                      borderColor: aampeTheme.colors.error,
                      '&:hover': {
                          borderColor: aampeTheme.colors.error,
                      },
                  }),
              }
            : undefined;

    return (
        <Container>
            <ElementContainer>
                <LabelContainer>
                    <HeadingText>{heading} </HeadingText>{' '}
                    <HandleBarText>
                        {`{{ ${element.sectionType} }}`}{' '}
                    </HandleBarText>
                </LabelContainer>
                <MapElementContent
                    element={element}
                    componentTypes={componentTypes}
                    datasetData={datasetData}
                />
            </ElementContainer>
            <ICONS.doubleRightArrow
                style={{ alignSelf: 'center', marginTop: '28px' }}
            />
            <MapContainer>
                <HeadingText>Map to</HeadingText>
                <TemplateSearchableSelect
                    name={element.sectionType}
                    options={filteredOptions}
                    onChange={(field, item) => {
                        setFieldValue(field, item);
                    }}
                    placeholder="Choose a Related Field"
                    isDisabled={false}
                    value={(values as any)[element.sectionType]}
                    customStyles={errorStyles}
                />
                {(values as any)[element.sectionType].value === null && (
                    <InlineNotification
                        displayIcon
                        status={ToastStatus.Warning}
                        title="None Selected"
                        description="If you select None, you lose the ability to map this field—and the content it contains won’t be preserved."
                    />
                )}
            </MapContainer>
        </Container>
    );
};

const Container = styled.div({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: aampeTheme.colors.white,
    padding: aampeTheme.spacing4(3),
    gap: aampeTheme.spacing4(2),
    justifyContent: 'start',
    borderRadius: aampeTheme.borderRadius.small,
    border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
});

const ElementContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(2),
    alignSelf: 'start',
    flex: 1,
});

const MapContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(2),
    flex: 1,
});
const HeadingText = styled.span({
    color: aampeTheme.colors.neutral09,
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.light,
    lineHeight: aampeTheme.size.lineHeight.default,
});
const HandleBarText = styled.span({
    color: aampeTheme.colors.neutral09,
    backgroundColor: aampeTheme.colors.backgroundWG02,
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.light,
    lineHeight: aampeTheme.size.lineHeight.default,
});

const TemplateSearchableSelect = styled(SearchableSelect)({
    width: '100%',
});
const LabelContainer = styled.div({
    display: 'flex',
    flexDirection: 'row',
    gap: aampeTheme.spacing4(2),
});

export default MapElement;
